import type { ButtonHTMLAttributes } from 'react';

/**
 * Button — the primary action control.
 *
 * Corpus: `PR1:70` (hero CTA, green fill, inverse ink),
 * `SI:62` (auth submit), `DB:254` (app new-issue
 * CTA, with a leading `+` glyph the caller supplies). Secondary is the bordered
 * white control `actGhost` at `DB:1846`.
 *
 * One divergence, disclosed rather than smoothed over: `actGhost`'s edge is
 * `1.5px solid #c7c7cc` and `button.css` draws 1px. The 1px predates the move
 * into tiers — it came from the input border, which is the other place #c7c7cc
 * appears — and thickening it now would restyle every secondary button in the
 * app for the sake of a directory reshuffle.
 *
 * The label is a child, never a literal inside this file: every string a person
 * reads is supplied by the caller from a translation catalogue.
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: 'primary' | 'secondary';
}

export function Button({ variant = 'primary', className, type, ...rest }: ButtonProps) {
  const classes = ['rp-button', `rp-button--${variant}`, className].filter(Boolean).join(' ');
  // Defaulting to `button` rather than the HTML default `submit` stops a button
  // dropped inside a form from submitting it by accident.
  return <button {...rest} type={type ?? 'button'} className={classes} />;
}
